/**
 * Dynamic Pricing System
 * Handles cart-based tiered discounts and price updates
 */

class DynamicPricing {
  constructor() {
    this.settings = this.loadSettings();
    this.currentTier = null;
    this.lastCartTotal = null;
    this.updateTimeout = null;
    this.init();
  }

  loadSettings() {
    // Load settings from window or data attributes
    const settingsElement = document.querySelector('[data-dynamic-pricing-settings]');
    if (settingsElement) {
      return JSON.parse(settingsElement.textContent);
    }
    
    // Fallback: read from Shopify settings if available
    return {
      enabled: window.dynamicPricingSettings?.enabled || false,
      discountType: window.dynamicPricingSettings?.discountType || 'fixed',
      fixedDiscount: window.dynamicPricingSettings?.fixedDiscount || 0,
      tiers: window.dynamicPricingSettings?.tiers || [
        { threshold: 5000, discount: 25 },  // $50 in cents
        { threshold: 10000, discount: 30 }, // $100 in cents
        { threshold: 15000, discount: 40 }  // $150 in cents
      ],
      collectionId: window.dynamicPricingSettings?.collectionId || null
    };
  }

  init() {
    if (!this.settings.enabled) return;

    // Update prices on page load
    this.updateAllPrices();

    // Listen for cart changes
    this.observeCartChanges();

    // Listen for cart updates via Shopify events
    document.addEventListener('cart:updated', () => {
      this.updateAllPrices();
    });

    // Listen for variant changes on product pages
    document.addEventListener('variant:change', (e) => {
      this.updateProductPrice(e.detail.variant);
    });
  }

  observeCartChanges() {
    // Debounced update function
    const debouncedUpdate = () => {
      if (this.updateTimeout) {
        clearTimeout(this.updateTimeout);
      }
      this.updateTimeout = setTimeout(() => {
        this.updateAllPrices();
      }, 300);
    };

    // Observe cart drawer and cart page changes with debouncing
    const cartObserver = new MutationObserver(debouncedUpdate);

    const cartDrawer = document.querySelector('cart-drawer');
    const cartPage = document.querySelector('.cart-page');
    
    if (cartDrawer) {
      cartObserver.observe(cartDrawer, { childList: true, subtree: false });
    }
    
    if (cartPage) {
      cartObserver.observe(cartPage, { childList: true, subtree: false });
    }

    // Listen for Shopify's native cart update events
    if (typeof Shopify !== 'undefined' && Shopify.theme) {
      // Hook into form submissions
      document.addEventListener('submit', (e) => {
        if (e.target.matches('form[action="/cart/add"]') || e.target.matches('[is="product-form"]')) {
          setTimeout(() => this.updateAllPrices(), 500);
        }
      });
    }

    // Listen for cart quantity changes
    document.addEventListener('change', (e) => {
      if (e.target.matches('[name="updates[]"]') || e.target.matches('[name^="quantity"]')) {
        debouncedUpdate();
      }
    });
  }

  async getCartTotal() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();
      
      // Calculate subtotal based on ORIGINAL prices (before any discounts)
      // This is important because tier thresholds should be based on original cart value
      let eligibleTotal = 0;
      
      if (this.settings.collectionId) {
        // Filter items by collection, use original price
        for (const item of cart.items) {
          const productCollections = item.product_collections || [];
          if (productCollections.includes(this.settings.collectionId)) {
            // Use original_line_price to get undiscounted total
            eligibleTotal += item.original_line_price || item.line_price;
          }
        }
      } else {
        // All items are eligible - calculate from original prices
        for (const item of cart.items) {
          eligibleTotal += item.original_line_price || item.line_price;
        }
      }
      
      // Store cart subtotal for gift callout (uses actual cart.total_price)
      this.cartSubtotal = cart.total_price;
      
      return eligibleTotal;
    } catch (error) {
      console.error('Error fetching cart:', error);
      return 0;
    }
  }

  getCurrentDiscount(cartTotal) {
    if (this.settings.discountType === 'fixed') {
      return this.settings.fixedDiscount;
    }

    // Tiered discount - find the highest applicable tier
    let applicableDiscount = 0;
    let currentTier = null;

    for (const tier of this.settings.tiers) {
      if (cartTotal >= tier.threshold) {
        applicableDiscount = tier.discount;
        currentTier = tier;
      }
    }

    this.currentTier = currentTier;
    return applicableDiscount;
  }

  calculateDiscountedPrice(originalPrice, discountPercentage) {
    if (!discountPercentage || discountPercentage === 0) {
      return originalPrice;
    }
    
    const multiplier = (100 - discountPercentage) / 100;
    return Math.round(originalPrice * multiplier);
  }

  formatMoney(cents) {
    // Use Shopify's formatMoney if available
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(cents, window.theme?.moneyFormat);
    }
    
    // Fallback formatting
    const format = window.theme?.moneyFormat || '${{amount}}';
    const value = (cents / 100).toFixed(2);
    const valueNoDecimals = Math.round(cents / 100).toString();
    const valueWithComma = value.replace('.', ',');
    
    return format
      .replace('{{amount}}', value)
      .replace('{{amount_no_decimals}}', valueNoDecimals)
      .replace('{{amount_with_comma_separator}}', valueWithComma)
      .replace('{{amount_no_decimals_with_comma_separator}}', valueNoDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, ','));
  }

  async updateAllPrices() {
    if (!this.settings.enabled) return;

    const cartTotal = await this.getCartTotal();
    const discountPercentage = this.getCurrentDiscount(cartTotal);

    // Find all dynamic price elements
    const priceElements = document.querySelectorAll('.dynamic-price[data-applies-discount="true"]');

    priceElements.forEach(priceElement => {
      const originalPrice = parseInt(priceElement.dataset.originalPrice);
      const discountType = priceElement.dataset.discountType;
      
      if (!originalPrice) return;

      // Only update if using tiered pricing, or if fixed and not yet applied
      if (discountType === 'tiered' || discountType === 'fixed') {
        const discountedPrice = this.calculateDiscountedPrice(originalPrice, discountPercentage);
        
        // Update the displayed prices
        const originalPriceDisplay = priceElement.querySelector('[data-original-price-display]');
        const discountedPriceDisplay = priceElement.querySelector('[data-discounted-price-display]');
        
        if (discountedPriceDisplay) {
          discountedPriceDisplay.textContent = this.formatMoney(discountedPrice);
        }

        // Show/hide original price based on whether discount applies
        if (originalPriceDisplay) {
          if (discountPercentage > 0) {
            originalPriceDisplay.style.display = '';
            originalPriceDisplay.textContent = this.formatMoney(originalPrice);
          } else {
            originalPriceDisplay.style.display = 'none';
          }
        }

        // Add visual feedback for tier changes
        if (discountType === 'tiered' && discountPercentage > 0) {
          priceElement.classList.add('price--tier-active');
          priceElement.dataset.currentTier = discountPercentage;
        } else {
          priceElement.classList.remove('price--tier-active');
          delete priceElement.dataset.currentTier;
        }
      }
    });

    // Update cart tier message
    this.updateCartMessage(cartTotal);

    // Update gift callout message
    this.updateGiftCallout();

    // Dispatch event for other scripts that might need to know
    document.dispatchEvent(new CustomEvent('dynamicpricing:updated', {
      detail: { cartTotal, discountPercentage, tier: this.currentTier }
    }));
  }

  updateCartMessage(cartTotal) {
    const messageElement = document.querySelector('.dynamic-pricing-cart-message');
    if (!messageElement) return;

    // Only update if cart total has actually changed (allow small rounding differences)
    if (this.lastCartTotal !== null && Math.abs(this.lastCartTotal - cartTotal) < 1) return;
    this.lastCartTotal = cartTotal;

    const tier1Threshold = parseInt(messageElement.dataset.tier1Threshold) || 0;
    const tier1Discount = parseInt(messageElement.dataset.tier1Discount) || 0;
    const tier2Threshold = parseInt(messageElement.dataset.tier2Threshold) || 0;
    const tier2Discount = parseInt(messageElement.dataset.tier2Discount) || 0;
    const tier3Threshold = parseInt(messageElement.dataset.tier3Threshold) || 0;
    const tier3Discount = parseInt(messageElement.dataset.tier3Discount) || 0;

    const messageText = messageElement.querySelector('[data-tier-message]');
    const messageIcon = messageElement.querySelector('.tier-message-icon');
    if (!messageText) return;

    let message = '';
    let isUnlocked = false;

    // Ensure we're working with whole numbers to avoid floating point issues
    const cartCents = Math.round(cartTotal);
    const tier1Cents = Math.round(tier1Threshold);
    const tier2Cents = Math.round(tier2Threshold);
    const tier3Cents = Math.round(tier3Threshold);

    if (cartCents >= tier3Cents) {
      message = `You've unlocked ${tier3Discount}% off! 🔥`;
      isUnlocked = true;
      if (messageIcon) messageIcon.textContent = '🎉';
    } else if (cartCents >= tier2Cents) {
      const needed = Math.max(1, tier3Cents - cartCents);
      message = `Spend ${this.formatMoney(needed)} more for ${tier3Discount}% off!`;
      if (messageIcon) messageIcon.textContent = '🎯';
    } else if (cartCents >= tier1Cents) {
      const needed = Math.max(1, tier2Cents - cartCents);
      message = `Spend ${this.formatMoney(needed)} more for ${tier2Discount}% off!`;
      if (messageIcon) messageIcon.textContent = '🎯';
    } else {
      const needed = Math.max(1, tier1Cents - cartCents);
      message = `Spend ${this.formatMoney(needed)} more for ${tier1Discount}% off!`;
      if (messageIcon) messageIcon.textContent = '🎯';
    }

    // Only add animation if message actually changed
    if (messageText.textContent !== message) {
      messageElement.classList.add('tier-message-updating');
      setTimeout(() => {
        messageElement.classList.remove('tier-message-updating');
      }, 300);
    }

    // Update message
    messageText.textContent = message;

    // Toggle unlocked state
    if (isUnlocked) {
      messageElement.classList.add('tier-unlocked');
    } else {
      messageElement.classList.remove('tier-unlocked');
    }
  }

  updateGiftCallout() {
    // Check if gift callout is enabled
    if (!this.settings.giftCallout || !this.settings.giftCallout.enabled) return;

    const giftCalloutElement = document.querySelector('.gift-callout-message');
    if (!giftCalloutElement) return;

    const threshold = this.settings.giftCallout.threshold;
    const cartSubtotal = this.cartSubtotal || 0;
    
    const messageText = giftCalloutElement.querySelector('[data-gift-message]');
    const messageIcon = giftCalloutElement.querySelector('.gift-message-icon');
    if (!messageText) return;

    let message = '';
    let isUnlocked = false;

    if (cartSubtotal >= threshold) {
      // Gift unlocked
      message = this.settings.giftCallout.unlockedMessage;
      isUnlocked = true;
      if (messageIcon) messageIcon.textContent = '🎁';
    } else {
      // Show how much more needed
      const needed = Math.max(1, threshold - cartSubtotal);
      const formattedAmount = this.formatMoney(needed);
      message = this.settings.giftCallout.message.replace('{amount}', formattedAmount);
      if (messageIcon) messageIcon.textContent = '🎯';
    }

    // Only add animation if message actually changed
    if (messageText.textContent !== message) {
      giftCalloutElement.classList.add('gift-message-updating');
      setTimeout(() => {
        giftCalloutElement.classList.remove('gift-message-updating');
      }, 300);
    }

    // Update message
    messageText.textContent = message;

    // Toggle unlocked state
    if (isUnlocked) {
      giftCalloutElement.classList.add('gift-unlocked');
    } else {
      giftCalloutElement.classList.remove('gift-unlocked');
    }
  }

  updateProductPrice(variant) {
    if (!variant || !this.settings.enabled) return;

    const priceElement = document.querySelector(`[data-product-id="${variant.product_id}"] .dynamic-price`);
    if (!priceElement) return;

    // Update the data attribute
    priceElement.dataset.originalPrice = variant.price;
    
    // Trigger a full update
    this.updateAllPrices();
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.dynamicPricing = new DynamicPricing();
  });
} else {
  window.dynamicPricing = new DynamicPricing();
}

// Export for use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = DynamicPricing;
}

